import React, { useEffect, useState, useCallback } from 'react';
import Icon from 'components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
import { getComputeWalletStatus, COMPUTE_PLANS, DAILY_FREE_CREDITS } from '../../../services/computeWallet';
import { useNavigate } from 'react-router-dom';

const COMPUTE_BUCKETS = [
  {
    key: 'video',
    label: 'Video Seconds',
    icon: 'Video',
    color: '#6366F1',
    remainingKey: 'videoSecondsRemaining',
    usedKey: 'videoSecondsUsed',
    quotaKey: 'videoSecondsQuota',
    unit: 's',
    dailyBonus: DAILY_FREE_CREDITS?.video_seconds,
  },
  {
    key: 'music',
    label: 'Music Generations',
    icon: 'Music',
    color: '#8B5CF6',
    remainingKey: 'musicRemaining',
    usedKey: 'musicUsed',
    quotaKey: 'musicQuota',
    unit: ' songs',
    dailyBonus: DAILY_FREE_CREDITS?.music_generations,
  },
  {
    key: 'game',
    label: 'Game Generations',
    icon: 'Gamepad2',
    color: '#10B981',
    remainingKey: 'gameRemaining',
    usedKey: 'gameUsed',
    quotaKey: 'gameQuota',
    unit: ' games',
    dailyBonus: DAILY_FREE_CREDITS?.game_generations,
  },
];

export default function ComputeWalletWidget({ compact = false, showTopUp = true }) {
  const { user, effectiveTier, isPlatformOwner } = useAuth();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadWallet = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getComputeWalletStatus(user?.id, effectiveTier || 'free', user?.email || '');
      setWallet(data);
    } catch (err) {
      setError('Could not load compute wallet.');
    } finally {
      setLoading(false);
    }
  }, [user?.id, effectiveTier]);

  useEffect(() => {
    loadWallet();
  }, [loadWallet]);

  if (!user) return null;

  const plan = COMPUTE_PLANS?.[effectiveTier] || COMPUTE_PLANS?.free;

  if (compact) {
    return (
      <div className="flex items-center gap-3 flex-wrap">
        {COMPUTE_BUCKETS?.map((bucket) => {
          const remaining = isPlatformOwner ? '∞' : loading ? '…' : (wallet?.[bucket?.remainingKey] ?? 0);
          const quota = isPlatformOwner ? '∞' : (wallet?.[bucket?.quotaKey] ?? 0);
          return (
            <div key={bucket?.key} className="flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: `${bucket?.color}18` }}>
                <Icon name={bucket?.icon} size={12} color={bucket?.color} />
              </div>
              <span className="text-xs font-bold" style={{ color: bucket?.color }}>
                {remaining}{bucket?.unit !== 's' ? '' : 's'}
              </span>
              <span className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>/ {quota}{bucket?.unit !== 's' ? '' : 's'}</span>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl p-4 flex flex-col gap-4"
      style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-sm)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.12)' }}>
            <Icon name="Wallet" size={16} color="#6366F1" />
          </div>
          <div>
            <p className="text-sm font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Compute Wallet</p>
            <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
              {isPlatformOwner ? 'Platform Owner — Unlimited' : `${wallet?.planLabel || plan?.label} Plan`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadWallet}
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors hover:opacity-70"
            style={{ background: 'var(--color-muted)' }}
            title="Refresh wallet"
          >
            <Icon name="RefreshCw" size={12} color="var(--color-muted-foreground)" />
          </button>
          {showTopUp && !isPlatformOwner && (
            <button
              onClick={() => navigate('/pricing')}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
              style={{ background: 'rgba(99,102,241,0.12)', color: '#6366F1', fontFamily: 'var(--font-caption)' }}
            >
              <Icon name="Plus" size={11} color="#6366F1" />
              Top Up
            </button>
          )}
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="flex items-center gap-2 p-2 rounded-lg" style={{ background: 'rgba(239,68,68,0.08)' }}>
          <Icon name="AlertCircle" size={13} color="#EF4444" />
          <span className="text-xs" style={{ color: '#EF4444' }}>{error}</span>
        </div>
      )}

      {/* Compute buckets */}
      <div className="flex flex-col gap-3">
        {COMPUTE_BUCKETS?.map((bucket) => {
          const remaining = wallet?.[bucket?.remainingKey] ?? 0;
          const used = wallet?.[bucket?.usedKey] ?? 0;
          const quota = wallet?.[bucket?.quotaKey] ?? 1;
          const percent = isPlatformOwner ? 0 : quota > 0 ? Math.min(100, Math.round((used / quota) * 100)) : 0;
          const isLow = !isPlatformOwner && percent > 80;
          const isEmpty = !isPlatformOwner && remaining <= 0;

          return (
            <div key={bucket?.key} className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: `${bucket?.color}15` }}>
                    <Icon name={bucket?.icon} size={12} color={bucket?.color} />
                  </div>
                  <span className="text-xs font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>
                    {bucket?.label}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  {isEmpty && (
                    <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444', fontFamily: 'var(--font-caption)' }}>Empty</span>
                  )}
                  {isLow && !isEmpty && (
                    <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(245,158,11,0.1)', color: '#F59E0B', fontFamily: 'var(--font-caption)' }}>Low</span>
                  )}
                  <span className="text-xs font-bold" style={{ color: isEmpty ? '#EF4444' : isLow ? '#F59E0B' : bucket?.color, fontFamily: 'var(--font-data)' }}>
                    {isPlatformOwner ? '∞' : loading ? '…' : remaining}{bucket?.unit}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-data)' }}>
                    / {isPlatformOwner ? '∞' : quota}{bucket?.unit}
                  </span>
                </div>
              </div>
              {/* Progress bar */}
              {!isPlatformOwner && (
                <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--color-border)' }}>
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${percent}%`,
                      background: isEmpty ? '#EF4444' : isLow ? '#F59E0B' : bucket?.color,
                    }}
                  />
                </div>
              )}
              {/* Daily bonus hint */}
              {!isPlatformOwner && effectiveTier === 'free' && (
                <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                  +{bucket?.dailyBonus}{bucket?.unit} free daily bonus
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Daily free credits notice */}
      {!isPlatformOwner && (
        <div
          className="flex items-start gap-2 p-2.5 rounded-xl"
          style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)' }}
        >
          <Icon name="Gift" size={13} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-semibold" style={{ color: 'var(--color-success)', fontFamily: 'var(--font-heading)' }}>Daily Free Credits</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
              +10s video · +1 music · +1 game added daily to boost your quota.
            </p>
          </div>
        </div>
      )}

      {/* Upgrade CTA */}
      {!isPlatformOwner && effectiveTier === 'free' && (
        <button
          onClick={() => navigate('/pricing')}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all hover:opacity-90"
          style={{ background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', color: 'white', fontFamily: 'var(--font-heading)' }}
        >
          <Icon name="Zap" size={13} color="white" />
          Upgrade for More Compute
        </button>
      )}
    </div>
  );
}
